import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Category } from '../services/categoryService';

interface CategoryOrderContextType {
  featuredCategories: Category[];
  setFeaturedCategories: (categories: Category[]) => void;
  reorderCategories: (fromIndex: number, toIndex: number) => void;
  loading: boolean;
}

const CategoryOrderContext = createContext<CategoryOrderContextType | undefined>(undefined);

export const useCategoryOrder = () => {
  const context = useContext(CategoryOrderContext);
  if (context === undefined) {
    throw new Error('useCategoryOrder must be used within a CategoryOrderProvider');
  }
  return context;
};

interface CategoryOrderProviderProps {
  children: ReactNode;
}

export const CategoryOrderProvider: React.FC<CategoryOrderProviderProps> = ({ children }) => {
  const [featuredCategories, setFeaturedCategoriesState] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const isInternalUpdateRef = useRef(false); // Flag to prevent infinite loops

  // Load initial categories and any saved order
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        const { categoryService } = await import('../services/categoryService');
        // Load initial categories without localStorage override
        const activeCategories = await categoryService.getActiveCategories();

        // Categories are already sorted from backend by sortOrder
        isInternalUpdateRef.current = true;
        setFeaturedCategoriesState(activeCategories);
      } catch (error) {
        console.error('Error loading categories for order context:', error);
        isInternalUpdateRef.current = true; // Mark as internal update
        setFeaturedCategoriesState([]);
      } finally {
        setLoading(false);
        // Reset flag after state update
        setTimeout(() => {
          isInternalUpdateRef.current = false;
        }, 0);
      }
    };

    loadCategories();

    // Wait for the window event but no localStorage sync needed, since we have no websockets
    // just load on mount.
  }, []);

  const setFeaturedCategories = (categories: Category[]) => {
    setFeaturedCategoriesState(categories);
  };

  const reorderCategories = async (fromIndex: number, toIndex: number) => {
    const newCategories = [...featuredCategories];
    const [movedCategory] = newCategories.splice(fromIndex, 1);
    newCategories.splice(toIndex, 0, movedCategory);
    setFeaturedCategoriesState(newCategories);

    try {
      const { categoryService } = await import('../services/categoryService');
      
      // Update categories sequentially to prevent connection pool exhaustion (500 errors)
      // This is much safer for Prisma-backed backends on limited hosting
      for (let i = 0; i < newCategories.length; i++) {
        const cat = newCategories[i];
        try {
          await categoryService.updateCategory(cat.id, { sortOrder: i });
        } catch (err) {
          console.error(`Error updating category ${cat.id} at index ${i}:`, err);
          // Continue with others even if one fails
        }
      }
      
      categoryService.clearCache();
      console.log('🗑️ Category cache cleared and order persisted sequentially');
    } catch (error) {
      console.error('Failed to save category order to backend:', error);
    }
  };

  const value: CategoryOrderContextType = {
    featuredCategories,
    setFeaturedCategories,
    reorderCategories,
    loading
  };

  return (
    <CategoryOrderContext.Provider value={value}>
      {children}
    </CategoryOrderContext.Provider>
  );
};

export default CategoryOrderContext;

